import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import Select from 'react-select';
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { DEFAULT_ACADEMIC_YEAR } from '../../../config/constants';

const SDExamAddForm = ({
  examData, // null for add mode
  examOptions, // For add mode - available exam options
  onSubmit,
  onCancel,
  isSaving
}) => {
  console.log('examData:', examData) // For edit mode - current exam data
  console.log('examOptions:', examOptions)
  const mode = 'add';
  // Debug the data structure for edit mode
  if (examData) {
    console.log('Edit mode - Full examData:', examData);
    console.log('Edit mode - examData.gender:', examData.gender);
    console.log('Edit mode - examData.category:', examData.category);
    console.log('Edit mode - examData.special_categories:', examData.special_categories);
  }
  
  // State for Add mode - track selected exam and its options
  const [selectedExam, setSelectedExam] = useState(null);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [genderOptions, setGenderOptions] = useState([]);
  const [specialCategoryOptions, setSpecialCategoryOptions] = useState([]);
  
  const { register, handleSubmit, control, watch, setValue, reset } = useForm({
    defaultValues: {
      cndt_exam_id: '',
      cndt_exam_name: '',
      cndt_exam_session: '',
      cndt_academicyear: '',
      cndt_gender: '',
      cndt_category: '',
      candidate_exam_special_category: [],
      cndt_exam_strank: '',
      cndt_exam_airank: '',
    }
  });

  const handleExamChange = (examId) => {
    console.log(examId)
    if (!examId) return;
    
    const exam = examOptions.find(exam => exam.id === examId);
    if (!exam) return;
    
    // Set the selected exam and its options
    setSelectedExam(exam);
    setCategoryOptions(exam.general_categories || []);
    setGenderOptions(exam.genders || []);
    setSpecialCategoryOptions(exam.special_categories || []);
    
    // Update form values
    setValue('cndt_exam_name', exam.exam_name);
    setValue('cndt_exam_session', exam.exam_session);
    setValue('cndt_exam_board', exam.exam_board);
  };

  // Debug: Log form data on submit
  const handleFormSubmit = (data) => {
    data.cndt_academicyear = DEFAULT_ACADEMIC_YEAR;
    onSubmit(data);
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
      {/* Exam Selection */}
      <div className="grid grid-cols-1 md:grid-cols-2 mt-4">
        <label className="text-sm font-medium text-gray-700">Exam *</label>
        {mode === 'edit' ? (
          <input 
            type="text" 
            disabled 
            readOnly
            value={watch('cndt_exam_name')}
            className="col-span-full border border-gray-200 rounded-lg bg-gray-100 text-gray-700"
          />
        ) : (
          <Controller
            name="cndt_exam_id"
            control={control}
            rules={{ required: 'Exam and session is required' }}
            render={({ field }) => (
              <Select
                {...field}
                value={examOptions.find(exam => exam.id === field.value) ? {
                  value: field.value,
                  label: `${examOptions.find(exam => exam.id === field.value)?.exam_name} - ${examOptions.find(exam => exam.id === field.value)?.exam_session}`
                } : null}
                placeholder="Select an exam and session"
                options={examOptions.map(exam => ({
                  value: exam.id,
                  label: `${exam.exam_name} - ${exam.exam_session}`
                }))}
                onChange={(selectedOption) => {
                  const value = typeof selectedOption === 'object' ? selectedOption?.value : selectedOption;
                  field.onChange(value);
                  handleExamChange(value);
                }}
                className="col-span-full"
                classNamePrefix="react-select"
                styles={{
                  control: (base) => ({
                    ...base,
                    border: '1px solid #d1d5db',
                    borderRadius: '0.5rem',
                    minHeight: '42px',
                    '&:hover': {
                      borderColor: '#10b981'
                    }
                  }),
                  placeholder: (base) => ({
                    ...base,
                    color: '#9ca3af'
                  })
                }}
              />
            )}
          />
        )}
      </div>

      {/* Categories */}
      {((mode === 'edit' && examData?.category && examData.category.length > 0) || 
        (mode === 'add' && categoryOptions.length > 0)) && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
          <div className="grid grid-cols-4">
            {(mode === 'edit' ? examData.category : categoryOptions).map((category) => {
              return (
                <label key={category.id} className="p-2 col-span-1 space-x-2 border rounded-lg 
                transition-colors cursor-pointer flex items-center hover:border-primary-500 hover:bg-primary-100"
                >
                  <input
                    type="radio"
                    name="cndt_category"
                    value={category.id}
                    {...register('cndt_category')}
                    className="text-primary-500 ring-primary-500"
                  />
                  <span className="truncate">{category.category_abbrev}</span>
                </label>
              );
            })}
          </div>
        </div>
      )}

      {/* Genders */}
      {((mode === 'edit' && examData?.gender && examData.gender.length > 0) || 
        (mode === 'add' && genderOptions.length > 0)) && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
          <div className="grid grid-cols-2">
            {(mode === 'edit' ? examData.gender : genderOptions).map((gender) => {
              return (
                <label key={gender.id} 
                  className="p-2 col-span-1 space-x-2 border rounded-lg 
                  transition-colors cursor-pointer flex items-center hover:border-primary-500 hover:bg-primary-100"
                >
                  <input
                    type="radio"
                    name="cndt_gender"
                    value={gender.id}
                    {...register('cndt_gender')}
                    className="text-primary-500 ring-primary-500 "
                  />
                  <span className="truncate">{gender.gender_description}</span>
                </label>
              );
            })}
          </div>
        </div>
      )}

      {/* Special Categories */}
      {((mode === 'edit' && examData?.special_categories && examData.special_categories.length > 0) || 
        (mode === 'add' && specialCategoryOptions.length > 0)) && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Special Categories</label>
          <div className="flex flex-row flex-wrap gap-2 items-stretch space-x-2 align-middle">
            {(mode === 'edit' ? examData.special_categories : specialCategoryOptions).map((category) => {
              const checked = Array.isArray(watch('candidate_exam_special_category'))
                ? watch('candidate_exam_special_category').includes(category.category_abbrev)
                : false;
              return (
                <label key={category.category_abbrev}
                  className={`inline-flex items-center gap-2 border rounded-lg p-2 transition-colors cursor-pointer
                    ${checked ? 'border-primary-500 bg-primary-100 shadow-md' : 'border-gray-200 hover:border-primary-300'}`}>
                  <input
                    type="checkbox"
                    value={category.category_abbrev}
                    {...register('candidate_exam_special_category')}
                    className="text-primary-500 ring-primary-500"
                  />
                  <span className="truncate">{category.category_abbrev}</span>
                </label>
              );
            })}
          </div>
        </div>
      )}

      {/* Ranks */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">All India Rank</label>
          <input
            type="number"
            {...register('cndt_exam_airank')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="Enter All India Rank"
          />
        </div>
        {/* State Rank - Only show for MHTCET exam */}
        {watch('cndt_exam_name') && watch('cndt_exam_name') === 'MHTCET' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">State Rank</label>
            <input
              type="number"
              {...register('cndt_exam_strank')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Enter State Rank"
            />
          </div>
        )}
      </div>

      {/* Buttons */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:space-x-4 pt-4">
        <button
          type="submit"
          disabled={isSaving}
          className="buttonClass w-full md:w-auto flex items-center justify-center space-x-2 px-6 py-2 transition-colors disabled:opacity-50"
        >
          {isSaving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              <span>Saving...</span>
            </>
          ) : (
            <>
              <CheckIcon className="w-4 h-4" />
              <span>{mode === 'edit' ? 'Update Exam' : 'Add Exam'}</span>
            </>
          )}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="buttonClass w-full md:w-auto flex items-center justify-center space-x-2 px-6 py-2 transition-colors"
        >
          <XMarkIcon className="w-4 h-4" />
          <span>Cancel</span>
        </button>
      </div>
    </form>
  );
};

export default SDExamAddForm;
